import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { FontSize } from "../../../../components/Text";
import { DashboardTodayResponseDto } from "../../../../model/dashboard/dto/DashboardTodayResponseDto";
import { useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { TestIDs } from "../../../../tests/types/testIds";
import { ThemeType } from "../../../../theme/Theme";
import { Rounded } from "../../../../theme/Rounded";
import { Shadow } from "../../../../theme/Shadow";
import { Spacing } from "../../../../theme/Spacing";
import { standardDate } from "../../../../utils/Date";
import { DashboardTodaySparkline } from "./DashboardTodaySparkline";

const Row = styled.div`
  display: grid;
  gap: ${Spacing.m};
  grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
`;

const Kpi = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
  min-width: 0;
  padding: ${Spacing.m};
  border-radius: ${Rounded.xl};
  background-color: ${({ theme }) => theme.white};
  box-shadow: ${Shadow.SM};
`;

const Label = styled.span`
  color: ${({ theme }) => theme.darkerSub};
  font-size: ${FontSize.s};
`;

const Value = styled.span`
  font-size: ${FontSize.xxl};
  font-weight: bold;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.textColor};
`;

const Note = styled.span<{ $tone: keyof ThemeType }>`
  font-size: ${FontSize.s};
  font-weight: 500;
  color: ${({ theme, $tone }) => theme[$tone]};
`;

type DashboardTodayKpisProps = {
  today: DashboardTodayResponseDto;
};

export const DashboardTodayKpis: React.FC<DashboardTodayKpisProps> = ({
  today,
}) => {
  const { t } = useTranslation();
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;

  const { entries, deals, tasks, chats } = today;
  const entriesDelta = entries ? entries.last7Days - entries.previous7Days : 0;

  return (
    <Row data-testid={TestIDs.dashboard.today.kpis}>
      {entries ? (
        <Kpi>
          <Label>{t("dashboard.today.kpi.entries")}</Label>
          <Value>{entries.last7Days}</Value>
          <Note
            $tone={
              entriesDelta < 0
                ? "danger"
                : entriesDelta > 0
                  ? "success"
                  : "darkerSub"
            }
          >
            {entriesDelta === 0
              ? t("dashboard.today.kpi.entries_same")
              : t(
                  entriesDelta < 0
                    ? "dashboard.today.kpi.entries_down"
                    : "dashboard.today.kpi.entries_up",
                  { count: Math.abs(entriesDelta) },
                )}
          </Note>
          <DashboardTodaySparkline values={entries.daily} />
        </Kpi>
      ) : null}

      {deals ? (
        <Kpi>
          <Label>{t("dashboard.today.kpi.deals")}</Label>
          <Value>{deals.open}</Value>
          <Note $tone="success">
            {t("dashboard.today.kpi.deals_won", { count: deals.wonLast7Days })}
          </Note>
        </Kpi>
      ) : null}

      <Kpi>
        <Label>{t("dashboard.today.kpi.tasks")}</Label>
        <Value>{tasks.dueToday}</Value>
        <Note $tone={tasks.overdue ? "danger" : "darkerSub"}>
          {t("dashboard.today.kpi.tasks_overdue", { count: tasks.overdue })}
        </Note>
      </Kpi>

      {chats ? (
        <Kpi>
          <Label>{t("dashboard.today.kpi.chats")}</Label>
          <Value>{chats.waiting}</Value>
          <Note $tone={chats.waiting ? "warning" : "darkerSub"}>
            {chats.oldestWaitingAt
              ? t("dashboard.today.kpi.chats_oldest", {
                  time: standardDate(chats.oldestWaitingAt, user, true),
                })
              : t("dashboard.today.kpi.chats_none")}
          </Note>
        </Kpi>
      ) : null}
    </Row>
  );
};
